import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'

export interface MarketplaceSettledGate {
  /** true only when settings have loaded AND this tenant has the feature. */
  enabled: boolean
  /** true only while the first fetch is in flight. */
  isLoading: boolean
  /** true when `['settings','all']` failed to load. */
  isError: boolean
}

/**
 * useMarketplaceSettledEnabled tells the UI whether this tenant may mark a
 * custom order type as "settled by marketplace" (F-MM-N23 — backend env
 * `FEATURE_MARKETPLACE_SETTLED=1`, surfaced as the server-injected
 * `feature_marketplace_settled` key on GET /settings — env-authoritative,
 * never stored in app_settings).
 *
 *   • enabled  → the order-type modal shows the tax-treatment controls and
 *                the counter honours `tax_treatment: 'marketplace_settled'`
 *   • disabled → every marketplace control is hidden and every order type
 *                prices as standard
 *
 * Shares the `['settings','all']` query cache with the rest of the app, so
 * this adds no extra network call. A missing key (old backend) reads as
 * disabled.
 */
export function useMarketplaceSettledEnabled(): MarketplaceSettledGate {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
  })
  const all = data?.data as Record<string, unknown> | undefined
  return {
    enabled: all?.feature_marketplace_settled === true,
    isLoading,
    isError,
  }
}
